/**
 * Makes `Error` readable in observability.
 *
 * Port of `montecarlo-tanstack/src/setup/errorLogging.ts`. `@decocms/blocks`'s
 * `patchConsole()` (sdk/otel.ts) swaps `console.*` for a shim whose
 * `forward()` builds the log line with
 * `try { return JSON.stringify(a) } catch { return String(a) }` for any
 * non-string argument. `Error` has no own enumerable property, so it
 * serializes to `{}` — that's the source of every blind `[VTEX] ... error: {}`
 * log line: the error reached observability, but its message, stack and
 * cause are gone.
 *
 * Why `Error.prototype.toJSON` and not a `console` wrapper: `bootObservability()`
 * runs INSIDE the fetch handler, not at module init, so a `console` patch
 * applied at module load ends up as the layer INSIDE it — `forward()`
 * serializes the raw arguments before ever reaching it (montecarlo's first
 * attempt at this fix, via a console wrapper, shipped and stayed a no-op in
 * production for exactly this reason). Re-applying the wrapper per-request
 * would work around the ordering, but `JSON.stringify` consults `toJSON` at
 * serialize time regardless of boot order — so this path doesn't depend on
 * init order at all, costs nothing per request, and also catches an `Error`
 * nested inside a plain object, which no console wrapper reaches.
 *
 * ponytail: one mechanism, not two. Don't add a parallel console wrapper for
 * the same consumer — it would be strictly less general than this.
 */

/**
 * `stack` already includes `name: message`. The fallback isn't theoretical:
 * the `DOMException` thrown by `AbortSignal.timeout()` — the fetch deadline
 * in `~/server/fetchTimeout.ts` — has no `stack`.
 */
function describe(error: Error): string {
  const head = error.stack ?? `${error.name}: ${error.message}`;

  // `code`/`retryable` and friends: a `DOMException` carries `code: 23` and
  // the VTEX client marks `retryable`. Neither is enumerable, so neither
  // JSON nor `stack` shows them — and they're exactly what says whether the
  // call is safe to retry.
  const extras = ["code", "retryable", "status"]
    .map((k) => [k, (error as unknown as Record<string, unknown>)[k]] as const)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k}=${String(v)}`);
  const withExtras = extras.length ? `${head} (${extras.join(" ")})` : head;

  // VTEX chains the underlying network/HTTP error onto `cause`; without this
  // the real reason disappears.
  const cause = (error as { cause?: unknown }).cause;
  if (cause instanceof Error) return `${withExtras}\ncaused by: ${describe(cause)}`;
  return cause === undefined ? withExtras : `${withExtras}\ncaused by: ${String(cause)}`;
}

/** Exported for tests; at runtime the caller is `JSON.stringify` itself. */
export function errorToJSON(this: Error): string {
  return describe(this);
}

// `enumerable: false` on purpose: `toJSON` must not show up in `Object.keys`
// or a `for…in` over an Error, or it leaks into any code that iterates props.
Object.defineProperty(Error.prototype, "toJSON", {
  value: errorToJSON,
  enumerable: false,
  configurable: true,
  writable: true,
});
